import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from './index';
import { GoalFormField } from '../types';
import { EWS_DURATION_MAP, EWS_TERM_TAB_KEY_MAP, TermTabType } from '../constants';
import { AdviceGoalPayloadType } from '../../features/client-goals';
import { selectEwsResponses } from '../../features/ews';
import { getAccountsByDuration } from '../../utils';

type GoalField = AdviceGoalPayloadType['formFields'][0];

const TERM_TABS: TermTabType[] = [TermTabType.SHORT, TermTabType.MEDIUM, TermTabType.LONG];

/**
 * Custom hook that manages goal term tab state (Short/Medium/Long)
 * and auto-selects the appropriate tab when formFields change.
 *
 * A goal is excluded from tab-selection when EITHER its tenure has no
 * available funding accounts OR its tenure's EWS response is loaded and
 * contains no allocation entry for the goal. While the tenure's EWS
 * response is missing/loading/errored, the goal is treated as having
 * funding (not filtered out) to avoid premature filtering.
 *
 * Shared between CreateGoalsStep and FundingNavigatorStep.
 */
export const useGoalTermTabs = () => {
    const global = useAppSelector((state) => state.global);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { formFields } = adviceGoals;

    const [selectedTermIndex, setSelectedTermIndex] = useState(TermTabType.SHORT);
    const [displayedFormFields, setDisplayedFormFields] = useState<GoalFormField[]>([]);

    const incomeSourceTypes = global?.orgInfo?.incomeSourceType?.en;
    const investmentAccountTypes = global?.orgInfo?.investmentAccountType?.en;
    const ewsResponses = useAppSelector(selectEwsResponses);

    // Per-tenure available account count (Short/Medium/Long).
    const tenureHasAccounts = useMemo(() => {
        return TERM_TABS.reduce((acc, term) => {
            const duration = EWS_DURATION_MAP[EWS_TERM_TAB_KEY_MAP[term]];
            acc[term] =
                getAccountsByDuration(adviceClient, incomeSourceTypes, investmentAccountTypes, duration).length > 0;
            return acc;
        }, {} as Record<TermTabType, boolean>);
    }, [adviceClient, incomeSourceTypes, investmentAccountTypes]);

    // Per-tenure set of goalIds that have at least one EWS allocation entry.
    // `null` means EWS for that tenure is missing/loading/errored — treat as has funding.
    const ewsAllocatedGoalIdsByTerm = useMemo(() => {
        return TERM_TABS.reduce((acc, term) => {
            const entry = ewsResponses?.[EWS_TERM_TAB_KEY_MAP[term]];
            const ewsList = entry?.response?.body?.ews as { goalId?: string }[] | undefined;
            if (!entry || entry.loading === true || entry.error === true || !ewsList) {
                acc[term] = null;
                return acc;
            }
            const ids = new Set<string>();
            ewsList.forEach((src) => {
                if (src?.goalId) ids.add(src.goalId);
            });
            acc[term] = ids;
            return acc;
        }, {} as Record<TermTabType, Set<string> | null>);
    }, [ewsResponses]);

    const filteredFormFields = useMemo<GoalField[]>(() => {
        if (!formFields?.length) return [];
        return formFields.filter((goal: GoalField) => {
            const term = (goal.data.goalCategoryOrder - 1) as TermTabType;
            // Exclude if the tenure has no available funding accounts.
            if (!tenureHasAccounts[term]) return false;
            const allocatedIds = ewsAllocatedGoalIdsByTerm[term];
            // EWS not yet known → don't exclude.
            if (allocatedIds === null) return true;
            // Exclude if the loaded EWS has no allocation entry for this goal.
            return allocatedIds.has((goal as any).goalId);
        });
    }, [formFields, tenureHasAccounts, ewsAllocatedGoalIdsByTerm]);

    const handleGoalTermTabChange = useCallback(
        (_event: React.SyntheticEvent<Element, Event> | null, newValue: TermTabType) => {
            setSelectedTermIndex(newValue);
            const filteredGoals = formFields.filter((goal: GoalField) => newValue === goal.data.goalCategoryOrder - 1);
            setDisplayedFormFields(filteredGoals);
        },
        [filteredFormFields]
    );

    /* The below useEffect decides which tab to land on when user comes
     * from Advisor Console (if !global?.uiAppState?.isPlanOpen) vs did add/edit
     * goals (else global?.uiAppState?.isPlanOpen)
     */
    useEffect(() => {
        if (!global.globalLoader) {
            // On the Create Goals step (step 2) we want to consider all goals,
            // including those without funding sources for their tenure.
            const effectiveFormFields = adviceClient?.step === 2 ? formFields : filteredFormFields;
            if (!global?.uiAppState?.isPlanOpen && effectiveFormFields?.length > 0) {
                const shortTermGoals = effectiveFormFields.filter(
                    (goal: GoalField) => TermTabType.SHORT === goal.data.goalCategoryOrder - 1
                );
                const mediumTermGoals = effectiveFormFields.filter(
                    (goal: GoalField) => TermTabType.MEDIUM === goal.data.goalCategoryOrder - 1
                );
                const longTermGoals = effectiveFormFields.filter(
                    (goal: GoalField) => TermTabType.LONG === goal.data.goalCategoryOrder - 1
                );
                if (shortTermGoals.length > 0) {
                    setSelectedTermIndex(TermTabType.SHORT);
                    setDisplayedFormFields(shortTermGoals);
                } else if (mediumTermGoals.length > 0) {
                    setSelectedTermIndex(TermTabType.MEDIUM);
                    setDisplayedFormFields(mediumTermGoals);
                } else if (longTermGoals.length > 0) {
                    setSelectedTermIndex(TermTabType.LONG);
                    setDisplayedFormFields(longTermGoals);
                }
            } else if (effectiveFormFields?.length > 0) {
                // Subsequent formFields changes (user adding/editing goals)
                // Land on the tab of the most recently updated (eligible) goal
                let latestTimestamp = dayjs(effectiveFormFields[0].data.createdAt);
                let latestUpdatedGoalCategoryOrder = effectiveFormFields[0].data.goalCategoryOrder;

                effectiveFormFields.forEach((goal: GoalField) => {
                    const createdAt = dayjs(goal.data.createdAt);
                    const updatedAt = goal.data.updatedAt ? dayjs(goal.data.updatedAt) : null;

                    // Get the most recent timestamp for this goal (updatedAt or createdAt)
                    const goalLatestTimestamp = updatedAt && updatedAt.isAfter(createdAt) ? updatedAt : createdAt;

                    // Compare to the overall latest timestamp
                    if (goalLatestTimestamp.isAfter(latestTimestamp)) {
                        latestTimestamp = goalLatestTimestamp;
                        latestUpdatedGoalCategoryOrder = goal.data.goalCategoryOrder;
                    }
                });
                handleGoalTermTabChange(null, latestUpdatedGoalCategoryOrder - 1);
            } else {
                handleGoalTermTabChange(null, TermTabType.SHORT);
            }
        }
    }, [filteredFormFields, formFields, adviceClient?.step, handleGoalTermTabChange]);

    return {
        selectedTermIndex,
        displayedFormFields,
        handleGoalTermTabChange,
        formFields
    };
};
